import { useEffect, useState } from 'react';
import { Building2, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { takeOrgChangedNotice } from '../../lib/orgGuard';

const SHOW_MS = 12_000;

/**
 * Says why a tab just landed on the dashboard: it was showing one
 * organisation while the account had moved to another (switched in another
 * tab or on another device), and `lib/orgGuard` reloaded it. Without this the
 * page a person was reading simply vanishes. Mounted once in App.
 */
function OrgChangedNotice() {
  const { user } = useAuth();
  // Read once, on the load that follows the reload; the flag is cleared by reading.
  const [visible, setVisible] = useState(takeOrgChangedNotice);

  useEffect(() => {
    if (!visible) return;
    const timer = window.setTimeout(() => setVisible(false), SHOW_MS);
    return () => window.clearTimeout(timer);
  }, [visible]);

  if (!visible || !user) return null;

  return (
    <div className="fixed inset-x-4 top-4 z-[1100] flex justify-center pointer-events-none">
      <div
        role="status"
        className="pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-xl border border-primary-200 bg-white p-4 shadow-lg"
      >
        <Building2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600" />
        <div className="min-w-0 flex-1 text-sm text-neutral-700">
          <p className="font-semibold text-neutral-900 break-words">
            {user.organisation_name ? `You are now in ${user.organisation_name}` : 'Your organisation changed'}
          </p>
          <p className="mt-1">
            This tab was showing a different organisation, so it was reloaded. Anything not saved on
            the previous page was not kept.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setVisible(false)}
          aria-label="Dismiss"
          className="flex-shrink-0 rounded-lg p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default OrgChangedNotice;
